import { native } from '@forest/native-services'
import type { IapProduct } from './iap-purchase'

// The Shop IAP price (GLOSSARY.md "Shop IAP price"): what native's store
// charges for a product's platform SKU, storefront-localized. Time Crystal
// packs and the starter pack alike.
export interface NativeIapPrice {
  priceLocalized: string
  priceValue: number
}

export type NativeIapPriceFailure =
  | 'invalid native price'
  | 'missing localized native price'
  | 'native price unavailable'

// Native answered, but with no usable price. A bridge failure rejects with
// its own error instead.
export class NativeIapPriceError extends Error {
  constructor(public readonly failure: NativeIapPriceFailure) {
    super(failure)
    this.name = 'NativeIapPriceError'
  }
}

export async function loadNativeIapPrice(
  product: IapProduct,
): Promise<NativeIapPrice> {
  const result = await native.iap.getPrice({
    product_id: product.productId,
    product_type: 'consumable',
    sku: product.sku,
  })
  if (!result.success) {
    throw new NativeIapPriceError('native price unavailable')
  }
  const { price_localized, price_value } = result.data
  if (
    typeof price_value !== 'number' ||
    !Number.isFinite(price_value) ||
    price_value <= 0
  ) {
    throw new NativeIapPriceError('invalid native price')
  }
  if (typeof price_localized !== 'string' || price_localized.length === 0) {
    throw new NativeIapPriceError('missing localized native price')
  }
  return { priceLocalized: price_localized, priceValue: price_value }
}
